import Parse from 'parse';
import { InjectionToken, Provider } from '@angular/core';
import { AssetTypeRepository, MethodTypeRepository } from '@telespot/domain';
import { ParseAssetTypeRepository, ParseMethodTypeRepository } from '@telespot/infrastructure/parse';
import { RetrieveMethodTypeQuery } from '@protocols/use-cases';
import { PaginatedStateLoadingMode } from '@shared/ui';

import { MethodTypeEditorEffects, RETRIEVE_METHOD_TYPE_QUERY } from '../../../effects/method-type-editor.effects';
import { assetTypeStoreProviders } from '../../../providers/store.providers';
import { MethodTypeEditorState } from '../../../state/method-type-editor.state';
import { MethodTypeState } from '../../../state/method-type.state';

import { MethodTypeEditorFacade } from './method-type-editor.facade';

const EDITOR_METHOD_TYPE_REPOSITORY = new InjectionToken<MethodTypeRepository>('EDITOR_METHOD_TYPE_REPOSITORY');
const EDITOR_ASSET_TYPE_REPOSITORY = new InjectionToken<AssetTypeRepository>('EDITOR_ASSET_TYPE_REPOSITORY');

/**
 * Providers for ONE method-type editing session: the editor trio, the retrieve
 * query over the session's repositories, and the asset-type catalog in
 * CONTINUOUS mode (the acquisition selector appends pages on scroll).
 *
 * `AssignedResourcesState` is deliberately NOT here even though
 * `MethodTypeEditorEffects` injects it (load seeds the resource tree): it is a
 * shared session ledger owned by the OUTERMOST consumer — the host page
 * provides the single instance (via its cascade-tier spread), stories provide
 * it directly. Same contract as the managers' staging ledgers.
 */
export const methodTypeEditorProviders: Provider[] = [
  MethodTypeEditorState,
  // The session's bound method type, published by the facade for the embedded
  // pipeline editing stack (the category editor's color action reads it).
  MethodTypeState,
  ...assetTypeStoreProviders(PaginatedStateLoadingMode.CONTINUOUS),
  {
    provide: EDITOR_METHOD_TYPE_REPOSITORY,
    useFactory: () => new ParseMethodTypeRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: EDITOR_ASSET_TYPE_REPOSITORY,
    useFactory: () => new ParseAssetTypeRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: RETRIEVE_METHOD_TYPE_QUERY,
    useFactory: (methodTypeRepository: MethodTypeRepository, assetTypeRepository: AssetTypeRepository) =>
      new RetrieveMethodTypeQuery(methodTypeRepository, assetTypeRepository),
    deps: [EDITOR_METHOD_TYPE_REPOSITORY, EDITOR_ASSET_TYPE_REPOSITORY],
  },
  MethodTypeEditorEffects,
  MethodTypeEditorFacade,
];
